/**
 * Steps 1 and 2 of the signup wizard: program identity, then the owner account.
 * Both validate inline against the same rules the tenant service enforces.
 */
import { TextField } from '../../components/text-field';
import { TRIAL } from '../../lib/constants';

import type { SlugCheckState } from './signup-validation';

export interface TeamStepValues {
  teamName: string;
  slug: string;
}

export interface TeamStepProps {
  values: TeamStepValues;
  errors: Partial<Record<'teamName' | 'slug', string>>;
  slugCheck: SlugCheckState;
  onChange: (patch: Partial<TeamStepValues>) => void;
  onSubmit: () => void;
}

/**
 * The portal address preview shown before the team code, derived from the
 * deployment the page is actually served from (`gridiron.torvanis.com/app/`
 * on that host, `localhost:5173/app/` in dev) rather than a hardcoded domain.
 */
export function portalUrlPrefix(): string {
  const host = typeof window === 'undefined' ? '' : window.location.host;
  return `${host}/app/`;
}

export function TeamStep({
  values,
  errors,
  slugCheck,
  onChange,
  onSubmit,
}: TeamStepProps): JSX.Element {
  return (
    <form
      className="stack"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div>
        <h2 className="auth__title">Name your program</h2>
        <p className="muted">
          The team code becomes your portal address and your staff&apos;s sign-in code. It cannot be
          changed later.
        </p>
      </div>
      <TextField
        label="Team or program name"
        name="team_name"
        required
        autoComplete="organization"
        autoFocus
        value={values.teamName}
        error={errors.teamName ?? null}
        placeholder="Westview Eagles"
        onChange={(event) => onChange({ teamName: event.target.value })}
      />
      <TextField
        label="Team code"
        name="tenant_slug"
        required
        inputMode="url"
        spellCheck={false}
        value={values.slug}
        error={errors.slug ?? null}
        prefix={portalUrlPrefix()}
        hint="Lower-case letters, numbers and single hyphens. 3–50 characters."
        status={<SlugStatus state={slugCheck} />}
        onChange={(event) => onChange({ slug: event.target.value.toLowerCase() })}
      />
      <button type="submit" className="btn btn--primary btn--block">
        Continue
      </button>
    </form>
  );
}

function SlugStatus({ state }: { state: SlugCheckState }): JSX.Element | null {
  if (state.status === 'checking') {
    return <span className="field__status field__status--busy">Checking availability…</span>;
  }
  if (state.status === 'available') {
    return <span className="field__status field__status--ok">✓ {state.slug} is available</span>;
  }
  if (state.status === 'unavailable') {
    return <span className="field__error">{state.slug} is already taken</span>;
  }
  if (state.status === 'error') {
    return (
      <span className="field__status field__status--busy">
        Availability check unavailable — the server re-checks when you submit.
      </span>
    );
  }
  return null;
}

export interface OwnerStepValues {
  email: string;
  displayName: string;
  password: string;
  confirmPassword: string;
}

export interface OwnerStepProps {
  values: OwnerStepValues;
  errors: Partial<Record<keyof OwnerStepValues, string>>;
  passwordRequirements: readonly string[];
  onChange: (patch: Partial<OwnerStepValues>) => void;
  onSubmit: () => void;
  onBack: () => void;
}

export function OwnerStep({
  values,
  errors,
  passwordRequirements,
  onChange,
  onSubmit,
  onBack,
}: OwnerStepProps): JSX.Element {
  return (
    <form
      className="stack"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div>
        <h2 className="auth__title">Create the Team Admin account</h2>
        <p className="muted">
          This first account owns billing, staff invitations and the stat catalog. Team Admin
          accounts require multi-factor authentication at first sign-in.
        </p>
      </div>
      <TextField
        label="Work email"
        name="owner_email"
        type="email"
        required
        autoComplete="email"
        autoFocus
        value={values.email}
        error={errors.email ?? null}
        onChange={(event) => onChange({ email: event.target.value })}
      />
      <TextField
        label="Your name"
        name="owner_display_name"
        autoComplete="name"
        value={values.displayName}
        error={errors.displayName ?? null}
        hint="Shown to your staff on plays and audit entries. Optional."
        onChange={(event) => onChange({ displayName: event.target.value })}
      />
      <TextField
        label="Password"
        name="owner_password"
        type="password"
        required
        autoComplete="new-password"
        value={values.password}
        error={errors.password ?? null}
        hint={passwordRequirements.join(' · ')}
        onChange={(event) => onChange({ password: event.target.value })}
      />
      <TextField
        label="Confirm password"
        name="confirm_password"
        type="password"
        required
        autoComplete="new-password"
        value={values.confirmPassword}
        error={errors.confirmPassword ?? null}
        onChange={(event) => onChange({ confirmPassword: event.target.value })}
      />
      <div className="plan-callout">
        <strong>Starting plan: {TRIAL.planDisplayName} trial</strong>
        <span className="plan-callout__price">
          $0 for {TRIAL.days} days · no card required · up to 50 seats
        </span>
        <span className="muted">
          Every new program starts on the {TRIAL.planDisplayName} trial with the full coaching
          feature set. On day {TRIAL.days} the account moves to {TRIAL.downgradePlanDisplayName}{' '}
          automatically; a Team Admin can choose a paid plan at any time from Billing.
        </span>
      </div>
      <div className="btn-row">
        <button type="submit" className="btn btn--primary">
          Continue to consent
        </button>
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          Back
        </button>
      </div>
    </form>
  );
}
